// Pulsar Economics — runtime defaults, validation, and v1→v2 migration.
// Schema spec: ./inputs.schema.md
// Type sidecar: ./inputs.d.ts
//
// All other engine modules (forecast, interchange, fx, earn, cashback,
// agentic, costs, summary, sensitivity, regions, pricing) consume DEFAULTS
// and validate before running. The website variable editor reads & writes
// the same shape via loadInputs / persistInputs.

const VERSION = '2.15.0';

// ---------------------------------------------------------------------------
// Tier registry — Free / Pro
// ---------------------------------------------------------------------------

const TIER_FREE = {
  key: 'free',
  displayName: 'Free',
  price: 0,
  priceAnnual: 0,
  fxCap: 100,
  fxAboveRate: 0.015,
  topupCap: 20,
  topupAboveRate: 0.02,
  swapCap: 0,
  swapAboveRate: 0.005,
  cashbackRate: 0,
  cashbackSpendCap: 0,
  aiAgentRunsIncluded: 5,
  aiAgentOverageRate: 0.1,
  earnApy: 0.02,
  earnApyPointsBonus: 0,
  earnAumPerUser: 150,
  pulsarYieldSpread: 0.0075,
  walletOperationCostCap: 0.25,
  freeAtmCap: 0,
  virtualCardsCount: 1,
  physicalCardCost: 8,
};

const TIER_PRO = {
  key: 'pro',
  displayName: 'Pro',
  price: 10,
  priceAnnual: 100,
  fxCap: 1000,
  fxAboveRate: 0.015,
  topupCap: 1000,
  topupAboveRate: 0.015,
  swapCap: 5000,
  swapAboveRate: 0.0025,
  cashbackRate: 0,
  cashbackSpendCap: 0,
  aiAgentRunsIncluded: 100,
  aiAgentOverageRate: 0.05,
  earnApy: 0.035,
  earnApyPointsBonus: 0,
  earnAumPerUser: 1500,
  pulsarYieldSpread: 0.005,
  walletOperationCostCap: 1,
  freeAtmCap: 200,
  virtualCardsCount: 5,
  physicalCardCost: 8,
};

const TIERS = { free: TIER_FREE, pro: TIER_PRO };

// ---------------------------------------------------------------------------
// Region map
// ---------------------------------------------------------------------------

// CAC defaults uniform across regions; retuned to the all-in app-account CAC
// now used in the investor view.
// Per-region tuning is a future v2.x adjustment.
const CAC_Y1 = 18,
  CAC_Y2 = 18,
  CAC_Y3 = 18;

const REGIONS = {
  eu: {
    key: 'eu',
    displayName: 'EU bloc',
    interchangeRate: 0.002,
    fxUsageRate: 0.18,
    fxAvgVolumeMo: 400,
    cacBlendedY1: CAC_Y1,
    cacBlendedY2: CAC_Y2,
    cacBlendedY3: CAC_Y3,
    kycRequiresExplicitActivation: false,
  },
  uk: {
    key: 'uk',
    displayName: 'United Kingdom',
    interchangeRate: 0.002,
    fxUsageRate: 0.2,
    fxAvgVolumeMo: 350,
    cacBlendedY1: CAC_Y1,
    cacBlendedY2: CAC_Y2,
    cacBlendedY3: CAC_Y3,
    kycRequiresExplicitActivation: false,
  },
  us: {
    key: 'us',
    displayName: 'United States',
    interchangeRate: 0.018,
    fxUsageRate: 0.15,
    fxAvgVolumeMo: 300,
    cacBlendedY1: CAC_Y1,
    cacBlendedY2: CAC_Y2,
    cacBlendedY3: CAC_Y3,
    kycRequiresExplicitActivation: false,
  },
  apac: {
    key: 'apac',
    displayName: 'APAC',
    interchangeRate: 0.012,
    fxUsageRate: 0.25,
    fxAvgVolumeMo: 500,
    cacBlendedY1: CAC_Y1,
    cacBlendedY2: CAC_Y2,
    cacBlendedY3: CAC_Y3,
    kycRequiresExplicitActivation: true,
  },
  latam: {
    key: 'latam',
    displayName: 'LATAM',
    interchangeRate: 0.02,
    fxUsageRate: 0.3,
    fxAvgVolumeMo: 400,
    cacBlendedY1: CAC_Y1,
    cacBlendedY2: CAC_Y2,
    cacBlendedY3: CAC_Y3,
    kycRequiresExplicitActivation: true,
  },
  mena: {
    key: 'mena',
    displayName: 'MENA',
    interchangeRate: 0.018,
    fxUsageRate: 0.28,
    fxAvgVolumeMo: 450,
    cacBlendedY1: CAC_Y1,
    cacBlendedY2: CAC_Y2,
    cacBlendedY3: CAC_Y3,
    kycRequiresExplicitActivation: true,
  },
  africa: {
    key: 'africa',
    displayName: 'Africa',
    interchangeRate: 0.022,
    fxUsageRate: 0.35,
    fxAvgVolumeMo: 350,
    cacBlendedY1: CAC_Y1,
    cacBlendedY2: CAC_Y2,
    cacBlendedY3: CAC_Y3,
    kycRequiresExplicitActivation: true,
  },
};

const REGIONAL_MIX = {
  y1: { eu: 0.65, uk: 0.05, us: 0.25, apac: 0.02, latam: 0.02, mena: 0.005, africa: 0.005 },
  y2: { eu: 0.55, uk: 0.07, us: 0.3, apac: 0.04, latam: 0.03, mena: 0.005, africa: 0.005 },
  y3: { eu: 0.45, uk: 0.08, us: 0.32, apac: 0.07, latam: 0.05, mena: 0.015, africa: 0.015 },
};

// ---------------------------------------------------------------------------
// Channel mix (CAC modeling)
// ---------------------------------------------------------------------------

const CHANNEL_MIX = {
  organic: { weight: 0.15, cpa: 0 },
  partnerships: { weight: 0.25, cpa: 12 },
  performance: { weight: 0.25, cpa: 30 },
  cryptoNative: { weight: 0.1, cpa: 14 },
  content: { weight: 0.05, cpa: 10 },
  referral: { weight: 0.2, cpa: 28 }, // Hybrid program — activation + 12-mo ongoing share. Schema 2.3.0.
};

const ACQUISITION = {
  cardActivationCost: { free: 1, pro: 3 },
};

const STABLECOIN = {
  smartContractYieldApy: 0.05,
  avgNetworkCostPerWalletOperation: 0.03,
};

// ---------------------------------------------------------------------------
// Pulsar Points (loyalty asset)
// ---------------------------------------------------------------------------

const POINTS = {
  earnRateByCategory: {
    online: 0,
    inStore: 0,
    stablecoinFunded: 0,
    default: 0,
    atm: 0,
    taxes: 0,
  },
  redemptionMix: {
    subWaiver: 0.5,
    marketplace: 0.3,
    crypto: 0.1,
    usdCash: 0.1,
  },
  costPerRedemption: {
    subWaiver: 0.003,
    marketplace: 0.0025,
    crypto: 0.008,
    usdCash: 0.008,
  },
  pointFaceValue: 0.01,
  breakageRate: 0.45,
};

// ---------------------------------------------------------------------------
// AI agentic payments
// ---------------------------------------------------------------------------

const AI_AGENTS = {
  enabled: false,
  runsIncluded: { free: 5, pro: 40 },
  overageRate: { free: 0.001, pro: 0.001 },
  outcomeBasedSavings: {
    adoption: 0.15,
    avgPerYear: 240,
    pulsarShare: 0.1,
  },
  merchantReferral: {
    y1Active: false,
    y2Volume: 50000,
    refRate: 0.02,
  },
  costPerRun: 0.001,
};

// ---------------------------------------------------------------------------
// Agentic Infra (B2B) — Fast × Pulsar partnership shape. Off by default.
// Pulsar shares founder-confirmed 2026-05-06; volume / rate fields are
// placeholders pending founder tuning at the editor.
// ---------------------------------------------------------------------------

const AGENTIC_INFRA = {
  enabled: true,
  portalUpgradeFee: {
    activeBusinesses: 125, // B2B loyalty / white-label base-case run-rate
    monthlyFeePerBusiness: 500, // EUR/mo gross — TBD pending founder
    pulsarShare: 0.7, // founder-confirmed 2026-05-06
  },
  settlementTxFee: {
    volumeMonthly: 35_000_000, // EUR/mo closed-loop payment volume
    ratePerTx: 0.003, // 0.30% — TBD pending founder
    pulsarShare: 0.3, // founder-confirmed 2026-05-06
  },
  paymentStreamingFee: {
    volumeMonthly: 8_000_000, // EUR/mo AI/B2B payment automation volume
    rate: 0.005, // TBD pending founder
    pulsarShare: 0.5, // founder-confirmed 2026-05-06
  },
  marketplaceFee: {
    volumeMonthly: 3_000_000, // EUR/mo partner campaign / loyalty GMV
    rate: 0.05, // TBD pending founder
    pulsarShare: 0.6, // founder-confirmed 2026-05-06
  },
};

// ---------------------------------------------------------------------------
// Card production — Pulsar's cost to manufacture + ship physical cards.
// Distinct from Tier.physicalCardCost (what the user pays).
// ---------------------------------------------------------------------------

const CARD_PRODUCTION = {
  costPerNewCard: { free: 8, pro: 8 },
  physicalAdoption: { free: 0.02, pro: 0.5 },
};

// ---------------------------------------------------------------------------
// Referral program (hybrid, founder-confirmed 2026-05-06)
// ---------------------------------------------------------------------------

const REFERRAL = {
  enabled: true,
  activationBonus: {
    referrerPoints: 100,
    refereePoints: 100,
    eligibilityMinSpendEur: 50,
    eligibilityWindowDays: 30,
  },
  ongoing: {
    rate: 0.01,
    durationMonths: 12,
    monthlyCapEur: 1000,
  },
  tierMultiplier: { free: 1.0, pro: 1.5 },
  adoptionRate: 0.15,
  avgReferralsPerReferrer: 1,
};

// ---------------------------------------------------------------------------
// Money Back — optional growth program. Defaults are zero, so the current
// plan is unchanged until an operator adds budget / funding / uplift.
// ---------------------------------------------------------------------------

const DROPS = {
  moneyBack: {
    pulsarMonthlyBudget: 1000,
    yieldShare: 0,
    yieldMonthlyCap: 0,
    proPaybackMonthlyBudget: 1000,
    proPaybackEligibleShare: 0,
    proPaybackRebate: 10,
    partnerSponsoredBudget: 1000,
    partnerCostShare: 0.2,
    avgRewardAmount: 15,
    fullImpactMonthlyBudget: 50_000,
    appAccountsPer10Reward: 3,
    cardActivationLift: 0.2,
    proConversionLift: 0.2,
    churnReduction: 0.05,
  },
};

// ---------------------------------------------------------------------------
// Token (placeholder — no Series A scaffolding at seed)
// ---------------------------------------------------------------------------

const TOKEN = { enabled: false };

// ---------------------------------------------------------------------------
// Wirex contract — deal-sheet facts
// ---------------------------------------------------------------------------

const WIREX = {
  termYears: 3,
  integrationFee: 0,
  kycVerificationFee: 0,
  transactionFeeRate: 0,
  interchangeShareY1: 0,
  interchangeShareY2: null,
  interchangeShareY3: null,
  tier1Share: 0.1,
  tier2Share: 0.3,
  tier3Share: 0.5,
  tier1Cap: 25_000_000,
  tier2Cap: 100_000_000,
  fxPassThrough: 0.005,
  floorY1: 0,
  floorY2: 3000,
  floorY3: 5000,
  disputeFee: 25,
  disputesPer1000Tx: 1,
  transactionsPerCardholderMo: 20,
  physicalCardMoq: 3000,
  earnYieldPartnerShare: 0.5, // pending Wirex Q(a) — placeholder
  exchangeFeeRate: 0.0025, // pending Wirex Q(b) — placeholder
};

const FX_RATES = { eurToUsd: 1.1, eurToGbp: 0.85 };

// ---------------------------------------------------------------------------
// Scenarios — two-tier model with subsidy distributions
// ---------------------------------------------------------------------------

function distScale(scale) {
  return scale === 'low' ? 0.5 : scale === 'high' ? 1.5 : 1.0;
}

function defaultSubsidies(scale) {
  const m = distScale(scale);
  return {
    fxCapHitRate: { p25: 0.05 * m, p50: 0.15 * m, p75: 0.3 * m },
    topupHeavyUserRate: { p25: 0.04 * m, p50: 0.12 * m, p75: 0.25 * m },
    swapHeavyUserRate: { p25: 0.03 * m, p50: 0.1 * m, p75: 0.22 * m },
    aiAgentHeavyUserRate: { p25: 0.05 * m, p50: 0.15 * m, p75: 0.3 * m },
  };
}

const SCENARIOS = {
  conservative: {
    netNewM1: 300,
    netNewM36: 1200,
    proConv: 0.12,
    churnFree: 0.03,
    churnPro: 0.02,
    activeRate: 0.7,
    cardActivationRate: 0.46,
    freeSpend: 350,
    proSpend: 1300,
    xborderPct: 0.12,
    freeTrading: 75,
    proTrading: 500,
    freeTopup: 100,
    proTopup: 700,
    freeAiActual: 1.5,
    proAiActual: 12,
    earnAdoptionRate: 0.25,
    freeWalletOperations: 0.4,
    proWalletOperations: 1.5,
    subsidies: defaultSubsidies('low'),
  },
  base: {
    netNewM1: 1500,
    netNewM36: 3000,
    proConv: 0.25,
    churnFree: 0.022,
    churnPro: 0.012,
    activeRate: 0.7,
    cardActivationRate: 0.46,
    freeSpend: 575,
    proSpend: 2300,
    xborderPct: 0.18,
    freeTrading: 150,
    proTrading: 1200,
    freeTopup: 150,
    proTopup: 1000,
    freeAiActual: 3,
    proAiActual: 25,
    earnAdoptionRate: 0.4,
    freeWalletOperations: 0.5,
    proWalletOperations: 2,
    subsidies: defaultSubsidies('mid'),
  },
  bull: {
    netNewM1: 2000,
    netNewM36: 8000,
    proConv: 0.138,
    churnFree: 0.016,
    churnPro: 0.01,
    activeRate: 0.7,
    cardActivationRate: 0.46,
    freeSpend: 650,
    proSpend: 1000,
    xborderPct: 0.22,
    freeTrading: 200,
    proTrading: 1700,
    freeTopup: 200,
    proTopup: 1500,
    freeAiActual: 4,
    proAiActual: 40,
    earnAdoptionRate: 0.5,
    freeWalletOperations: 0.7,
    proWalletOperations: 3,
    subsidies: defaultSubsidies('high'),
  },
};

// ---------------------------------------------------------------------------
// DEFAULTS
// ---------------------------------------------------------------------------

export const DEFAULTS = {
  version: VERSION,
  tiers: TIERS,
  regions: REGIONS,
  regionalMix: REGIONAL_MIX,
  channelMix: CHANNEL_MIX,
  fixedOpexMonthly: 80_000,
  acquisition: ACQUISITION,
  stablecoin: STABLECOIN,
  points: POINTS,
  aiAgents: AI_AGENTS,
  agenticInfra: AGENTIC_INFRA,
  referral: REFERRAL,
  drops: DROPS,
  cardProduction: CARD_PRODUCTION,
  token: TOKEN,
  wirex: WIREX,
  fx: FX_RATES,
  cardFundedTopupShare: 0.1, // Wirex rails reduce reliance on expensive card-funded top-ups
  scenarios: SCENARIOS,
  startingUsers: 500,
  startingProMix: 0.08,
  startingCumGmv: 5000,
  modelStartMonth: { year: 2026, month: 6 },
};

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

const TIER_KEYS = ['free', 'pro'];
const REGION_KEYS = ['eu', 'uk', 'us', 'apac', 'latam', 'mena', 'africa'];
const SCENARIO_KEYS = ['conservative', 'base', 'bull'];

function sumApprox(actual, target = 1.0, eps = 1e-6) {
  return Math.abs(actual - target) < eps;
}

export function validateInputs(inputs) {
  const errors = [];
  const push = (path, message) => errors.push({ path, message });

  if (!inputs || typeof inputs !== 'object') {
    push('', 'inputs must be an object');
    return errors;
  }

  if (!/^\d+\.\d+\.\d+$/.test(inputs.version || '')) {
    push('version', `must be semver (got "${inputs.version}")`);
  }

  for (const y of ['y1', 'y2', 'y3']) {
    const mix = inputs.regionalMix?.[y];
    if (!mix) {
      push(`regionalMix.${y}`, 'missing');
      continue;
    }
    const total = Object.values(mix).reduce((a, n) => a + n, 0);
    if (!sumApprox(total))
      push(`regionalMix.${y}`, `shares sum to ${total.toFixed(4)}, expected 1.0`);
    for (const rk of Object.keys(mix)) {
      if (!REGION_KEYS.includes(rk)) push(`regionalMix.${y}.${rk}`, 'unknown region');
    }
  }

  const channelTotal = Object.values(inputs.channelMix || {}).reduce(
    (a, c) => a + (c?.weight ?? 0),
    0,
  );
  if (!sumApprox(channelTotal))
    push('channelMix', `weights sum to ${channelTotal.toFixed(4)}, expected 1.0`);

  if (!(inputs.fixedOpexMonthly >= 0)) {
    push('fixedOpexMonthly', 'must be ≥ 0');
  }

  for (const tk of TIER_KEYS) {
    const cost = inputs.acquisition?.cardActivationCost?.[tk];
    if (typeof cost !== 'number' || cost < 0) {
      push(`acquisition.cardActivationCost.${tk}`, 'must be ≥ 0');
    }
  }

  if (!(inputs.stablecoin?.smartContractYieldApy >= 0)) {
    push('stablecoin.smartContractYieldApy', 'must be ≥ 0');
  }
  if (!(inputs.stablecoin?.avgNetworkCostPerWalletOperation >= 0)) {
    push('stablecoin.avgNetworkCostPerWalletOperation', 'must be ≥ 0');
  }

  const redTotal = Object.values(inputs.points?.redemptionMix || {}).reduce((a, n) => a + n, 0);
  if (!sumApprox(redTotal))
    push('points.redemptionMix', `mix sums to ${redTotal.toFixed(4)}, expected 1.0`);

  for (const k of TIER_KEYS) {
    const t = inputs.tiers?.[k];
    if (!t) {
      push(`tiers.${k}`, 'missing');
      continue;
    }
    if (t.price < 0) push(`tiers.${k}.price`, 'must be ≥ 0');
    if (t.priceAnnual > t.price * 12 + 1e-6) {
      push(`tiers.${k}.priceAnnual`, `${t.priceAnnual} > price*12 (${t.price * 12})`);
    }
    if ((t.earnApy || 0) + (t.earnApyPointsBonus || 0) > 0.2 + 1e-6) {
      push(
        `tiers.${k}.earnApy+Bonus`,
        `${(t.earnApy + t.earnApyPointsBonus).toFixed(3)} exceeds 0.20 sanity ceiling`,
      );
    }
    if (!(t.pulsarYieldSpread >= 0)) push(`tiers.${k}.pulsarYieldSpread`, 'must be ≥ 0');
    if ((t.pulsarYieldSpread || 0) > (inputs.stablecoin?.smartContractYieldApy || 0) + 1e-6) {
      push(`tiers.${k}.pulsarYieldSpread`, 'cannot exceed smart contract yield');
    }
    if (!(t.walletOperationCostCap >= 0)) push(`tiers.${k}.walletOperationCostCap`, 'must be ≥ 0');
  }

  const w = inputs.wirex;
  if (w) {
    for (const field of [
      'interchangeShareY1',
      'tier1Share',
      'tier2Share',
      'tier3Share',
      'fxPassThrough',
      'earnYieldPartnerShare',
      'exchangeFeeRate',
    ]) {
      const value = w[field];
      if (typeof value !== 'number' || value < 0 || value > 1) {
        push(`wirex.${field}`, 'must be a decimal in [0, 1]');
      }
    }
    for (const field of [
      'termYears',
      'integrationFee',
      'kycVerificationFee',
      'transactionFeeRate',
      'floorY1',
      'floorY2',
      'floorY3',
      'disputeFee',
      'disputesPer1000Tx',
      'transactionsPerCardholderMo',
      'physicalCardMoq',
    ]) {
      const value = w[field];
      if (typeof value !== 'number' || value < 0) {
        push(`wirex.${field}`, 'must be ≥ 0');
      }
    }
    if (w.tier1Cap >= w.tier2Cap) push('wirex.tier1Cap', 'must be < tier2Cap');
    if (w.tier1Share > w.tier2Share || w.tier2Share > w.tier3Share) {
      push('wirex.tierShares', 'must be monotonically non-decreasing');
    }
  }

  for (const k of TIER_KEYS) {
    if (typeof inputs.aiAgents?.runsIncluded?.[k] !== 'number')
      push(`aiAgents.runsIncluded.${k}`, 'missing or non-number');
    if (typeof inputs.aiAgents?.overageRate?.[k] !== 'number')
      push(`aiAgents.overageRate.${k}`, 'missing or non-number');
  }

  if (
    inputs.cardFundedTopupShare !== undefined &&
    !(inputs.cardFundedTopupShare >= 0 && inputs.cardFundedTopupShare <= 1)
  ) {
    push('cardFundedTopupShare', 'must be in [0, 1]');
  }

  // card production
  const cp = inputs.cardProduction;
  if (cp) {
    for (const tk of TIER_KEYS) {
      const cost = cp.costPerNewCard?.[tk];
      if (typeof cost !== 'number' || cost < 0) {
        push(`cardProduction.costPerNewCard.${tk}`, 'must be ≥ 0');
      }
      const adopt = cp.physicalAdoption?.[tk];
      if (typeof adopt !== 'number' || adopt < 0 || adopt > 1) {
        push(`cardProduction.physicalAdoption.${tk}`, 'must be in [0, 1]');
      }
    }
  }

  // referral program
  const r = inputs.referral;
  if (r) {
    if (!(r.ongoing?.rate >= 0 && r.ongoing?.rate <= 1))
      push('referral.ongoing.rate', 'must be in [0, 1]');
    if (!(r.adoptionRate >= 0 && r.adoptionRate <= 1))
      push('referral.adoptionRate', 'must be in [0, 1]');
    if (!(r.avgReferralsPerReferrer >= 0)) push('referral.avgReferralsPerReferrer', 'must be ≥ 0');
    if (!(r.ongoing?.durationMonths >= 0)) push('referral.ongoing.durationMonths', 'must be ≥ 0');
    if (!(r.ongoing?.monthlyCapEur >= 0)) push('referral.ongoing.monthlyCapEur', 'must be ≥ 0');
    for (const tk of TIER_KEYS) {
      if (typeof r.tierMultiplier?.[tk] !== 'number' || r.tierMultiplier[tk] < 0) {
        push(`referral.tierMultiplier.${tk}`, 'missing or negative');
      }
    }
    for (const k of [
      'referrerPoints',
      'refereePoints',
      'eligibilityMinSpendEur',
      'eligibilityWindowDays',
    ]) {
      if (typeof r.activationBonus?.[k] !== 'number' || r.activationBonus[k] < 0) {
        push(`referral.activationBonus.${k}`, 'must be a non-negative number');
      }
    }
  }

  // Money Back growth program
  const drops = inputs.drops;
  if (drops) {
    const moneyBack = drops.moneyBack;
    if (!moneyBack) {
      push('drops.moneyBack', 'missing');
    } else {
      for (const field of [
        'pulsarMonthlyBudget',
        'yieldShare',
        'yieldMonthlyCap',
        'proPaybackMonthlyBudget',
        'proPaybackEligibleShare',
        'proPaybackRebate',
        'partnerSponsoredBudget',
        'partnerCostShare',
        'avgRewardAmount',
        'fullImpactMonthlyBudget',
        'appAccountsPer10Reward',
        'cardActivationLift',
        'proConversionLift',
        'churnReduction',
      ]) {
        if (!(moneyBack[field] >= 0)) push(`drops.moneyBack.${field}`, 'must be ≥ 0');
      }
      for (const field of ['yieldShare', 'proPaybackEligibleShare', 'partnerCostShare']) {
        if (!(moneyBack[field] >= 0 && moneyBack[field] <= 1)) {
          push(`drops.moneyBack.${field}`, 'must be in [0, 1]');
        }
      }
    }
  }

  // agenticInfra (only enforced when enabled)
  if (inputs.agenticInfra?.enabled) {
    const lines = ['portalUpgradeFee', 'settlementTxFee', 'paymentStreamingFee', 'marketplaceFee'];
    for (const k of lines) {
      const ln = inputs.agenticInfra[k];
      if (!ln) {
        push(`agenticInfra.${k}`, 'missing');
        continue;
      }
      if (!(ln.pulsarShare >= 0 && ln.pulsarShare <= 1)) {
        push(`agenticInfra.${k}.pulsarShare`, `${ln.pulsarShare} not in [0, 1]`);
      }
      for (const f of Object.keys(ln)) {
        if (f === 'pulsarShare') continue;
        if (typeof ln[f] !== 'number' || ln[f] < 0) {
          push(`agenticInfra.${k}.${f}`, 'must be a number ≥ 0');
        }
      }
    }
  }

  for (const sk of SCENARIO_KEYS) {
    const s = inputs.scenarios?.[sk];
    if (!s) {
      push(`scenarios.${sk}`, 'missing');
      continue;
    }
    if ((s.proConv || 0) > 1 + 1e-6) {
      push(`scenarios.${sk}.proConv`, `proConv = ${s.proConv.toFixed(3)} > 1.0`);
    }
    if (!(s.cardActivationRate >= 0 && s.cardActivationRate <= 1)) {
      push(`scenarios.${sk}.cardActivationRate`, 'must be in [0, 1]');
    }
    for (const field of ['freeWalletOperations', 'proWalletOperations']) {
      if (!(s[field] >= 0)) push(`scenarios.${sk}.${field}`, 'must be ≥ 0');
    }
    const subs = s.subsidies || {};
    for (const dKey of Object.keys(subs)) {
      const d = subs[dKey];
      if (!(d?.p25 <= d?.p50 && d?.p50 <= d?.p75)) {
        push(`scenarios.${sk}.subsidies.${dKey}`, 'p25 ≤ p50 ≤ p75 violated');
      }
    }
  }

  return errors;
}

export function assertValidInputs(inputs) {
  const errs = validateInputs(inputs);
  if (errs.length) {
    const lines = errs.map((e) => `  ${e.path}: ${e.message}`).join('\n');
    throw new Error(`Invalid Pulsar inputs:\n${lines}`);
  }
}

// ---------------------------------------------------------------------------
// Migrations — every breaking schema change ships a step here.
// Loader walks from stored.version to DEFAULTS.version.
// ---------------------------------------------------------------------------

export const MIGRATIONS = [
  {
    from: '1.0.0',
    to: '2.0.0',
    apply(old) {
      // v1 was a flat PULSAR_DEFAULTS object. v2 is nested by domain.
      // We carry over every v1 field that has a v2 home; defaults fill the rest.
      const next = JSON.parse(JSON.stringify(DEFAULTS));

      // Tiers
      if (typeof old.proPrice === 'number') next.tiers.pro.price = old.proPrice;
      if (typeof old.proFxCap === 'number') next.tiers.pro.fxCap = old.proFxCap;
      if (typeof old.proFxOverageRate === 'number')
        next.tiers.pro.fxAboveRate = old.proFxOverageRate;
      if (typeof old.freeAiCap === 'number') next.tiers.free.aiAgentRunsIncluded = old.freeAiCap;
      if (typeof old.proAiCap === 'number') next.tiers.pro.aiAgentRunsIncluded = old.proAiCap;
      if (typeof old.freeFxFee === 'number') next.tiers.free.fxAboveRate = old.freeFxFee;

      // Wirex
      if (typeof old.wirexTier1Share === 'number') next.wirex.tier1Share = old.wirexTier1Share;
      if (typeof old.wirexTier2Share === 'number') next.wirex.tier2Share = old.wirexTier2Share;
      if (typeof old.wirexTier3Share === 'number') next.wirex.tier3Share = old.wirexTier3Share;
      if (typeof old.wirexTier1Cap === 'number') next.wirex.tier1Cap = old.wirexTier1Cap;
      if (typeof old.wirexTier2Cap === 'number') next.wirex.tier2Cap = old.wirexTier2Cap;
      if (typeof old.wirexFxPassThrough === 'number')
        next.wirex.fxPassThrough = old.wirexFxPassThrough;
      if (typeof old.wirexFloorY1 === 'number') next.wirex.floorY1 = old.wirexFloorY1;
      if (typeof old.wirexFloorY2 === 'number') next.wirex.floorY2 = old.wirexFloorY2;
      if (typeof old.wirexFloorY3 === 'number') next.wirex.floorY3 = old.wirexFloorY3;

      // Points (v1 had scaffolding but engine.js disabled it)
      if (typeof old.pointValue === 'number') next.points.pointFaceValue = old.pointValue;
      if (typeof old.redemptionMixPro === 'number')
        next.points.redemptionMix.subWaiver = old.redemptionMixPro;
      if (typeof old.redemptionMixWaiver === 'number')
        next.points.redemptionMix.marketplace = old.redemptionMixWaiver;
      if (typeof old.redemptionMixCashback === 'number')
        next.points.redemptionMix.usdCash = old.redemptionMixCashback;
      if (typeof old.redemptionMixOther === 'number')
        next.points.redemptionMix.usdCash = old.redemptionMixOther;
      if (typeof old.costPerPtPro === 'number')
        next.points.costPerRedemption.subWaiver = old.costPerPtPro;
      if (typeof old.costPerPtWaiver === 'number')
        next.points.costPerRedemption.marketplace = old.costPerPtWaiver;
      if (typeof old.costPerPtCashback === 'number')
        next.points.costPerRedemption.usdCash = old.costPerPtCashback;
      if (typeof old.costPerPtOther === 'number')
        next.points.costPerRedemption.usdCash = old.costPerPtOther;

      // FX & bootstrap
      if (typeof old.eurToUsd === 'number') next.fx.eurToUsd = old.eurToUsd;
      if (typeof old.startingUsers === 'number') next.startingUsers = old.startingUsers;
      if (typeof old.startingProMix === 'number') next.startingProMix = old.startingProMix;
      if (typeof old.startingCumGmv === 'number') next.startingCumGmv = old.startingCumGmv;

      // Scenarios — carry common fields, fold v1's scalar proHitCapPct into a triplet
      for (const sk of SCENARIO_KEYS) {
        const oldS = old.scenarios?.[sk];
        if (!oldS) continue;
        const newS = next.scenarios[sk];
        const carry = [
          'netNewM1',
          'netNewM36',
          'proConv',
          'churnFree',
          'churnPro',
          'freeSpend',
          'proSpend',
          'xborderPct',
          'freeTrading',
          'proTrading',
          'activeRate',
          'freeAiActual',
          'proAiActual',
        ];
        for (const k of carry) {
          if (typeof oldS[k] === 'number') newS[k] = oldS[k];
        }
        if (typeof oldS.proHitCapPct === 'number') {
          newS.subsidies.fxCapHitRate.p50 = oldS.proHitCapPct;
          newS.subsidies.fxCapHitRate.p25 = Math.max(0, oldS.proHitCapPct * 0.5);
          newS.subsidies.fxCapHitRate.p75 = Math.min(1, oldS.proHitCapPct * 1.8);
        }
      }

      next.version = '2.0.0';
      return next;
    },
  },
  {
    from: '2.0.0',
    to: '2.1.0',
    apply(old) {
      const next = JSON.parse(JSON.stringify(old));
      if (!next.agenticInfra) {
        next.agenticInfra = JSON.parse(JSON.stringify(DEFAULTS.agenticInfra));
      }
      if (next.regions) {
        for (const k of Object.keys(next.regions)) {
          const r = next.regions[k];
          if (typeof r.cacBlended === 'number' && typeof r.cacBlendedY1 !== 'number') {
            // Carry the v2.0 scalar into Y1, scale up for Y2/Y3 (warm-base ramp).
            r.cacBlendedY1 = r.cacBlended;
            r.cacBlendedY2 = r.cacBlended * 2.25;
            r.cacBlendedY3 = r.cacBlended * 2.75;
            delete r.cacBlended;
          } else if (typeof r.cacBlendedY1 !== 'number') {
            r.cacBlendedY1 = 8;
            r.cacBlendedY2 = 18;
            r.cacBlendedY3 = 22;
          }
        }
      }
      next.version = '2.1.0';
      return next;
    },
  },
  {
    from: '2.1.0',
    to: '2.2.0',
    apply(old) {
      const next = JSON.parse(JSON.stringify(old));
      if (typeof next.cardFundedTopupShare !== 'number') {
        next.cardFundedTopupShare = 0.3;
      }
      next.version = '2.2.0';
      return next;
    },
  },
  {
    from: '2.2.0',
    to: '2.3.0',
    apply(old) {
      const next = JSON.parse(JSON.stringify(old));
      if (!next.referral) {
        next.referral = JSON.parse(JSON.stringify(DEFAULTS.referral));
      }
      if (!next.cardProduction) {
        next.cardProduction = JSON.parse(JSON.stringify(DEFAULTS.cardProduction));
      }
      // Channel mix: add the referral channel; if organic still at the v2.2 default
      // weight (0.30), drop it to 0.20 so the new sum stays 1.0.
      if (next.channelMix && !next.channelMix.referral) {
        next.channelMix.referral = { weight: 0.1, cpa: 25 };
        if (next.channelMix.organic && Math.abs(next.channelMix.organic.weight - 0.3) < 1e-6) {
          next.channelMix.organic.weight = 0.2;
        }
      }
      next.version = '2.3.0';
      return next;
    },
  },
  {
    from: '2.3.0',
    to: '2.4.0',
    apply(old) {
      const next = JSON.parse(JSON.stringify(old));
      if (typeof next.fixedOpexMonthly !== 'number') {
        next.fixedOpexMonthly = DEFAULTS.fixedOpexMonthly;
      }
      if (!next.acquisition) {
        next.acquisition = JSON.parse(JSON.stringify(DEFAULTS.acquisition));
      }
      if (!next.stablecoin) {
        next.stablecoin = JSON.parse(JSON.stringify(DEFAULTS.stablecoin));
      }
      if (!next.aiAgents) {
        next.aiAgents = JSON.parse(JSON.stringify(DEFAULTS.aiAgents));
      }
      if (typeof next.aiAgents.enabled !== 'boolean') {
        next.aiAgents.enabled = DEFAULTS.aiAgents.enabled;
      }
      for (const tk of TIER_KEYS) {
        if (!next.tiers?.[tk]) continue;
        const defaults = DEFAULTS.tiers[tk];
        if (typeof next.tiers[tk].pulsarYieldSpread !== 'number') {
          next.tiers[tk].pulsarYieldSpread = defaults.pulsarYieldSpread;
        }
        if (typeof next.tiers[tk].walletOperationCostCap !== 'number') {
          next.tiers[tk].walletOperationCostCap = defaults.walletOperationCostCap;
        }
      }
      for (const sk of SCENARIO_KEYS) {
        if (!next.scenarios?.[sk]) continue;
        const defaults = DEFAULTS.scenarios[sk];
        for (const k of ['cardActivationRate', 'freeWalletOperations', 'proWalletOperations']) {
          if (typeof next.scenarios[sk][k] !== 'number') next.scenarios[sk][k] = defaults[k];
        }
      }
      next.version = '2.4.0';
      return next;
    },
  },
  {
    from: '2.4.0',
    to: '2.5.0',
    apply(old) {
      const next = JSON.parse(JSON.stringify(old));

      delete next.tiers?.ultimate;
      delete next.acquisition?.cardActivationCost?.ultimate;
      delete next.aiAgents?.runsIncluded?.ultimate;
      delete next.aiAgents?.overageRate?.ultimate;
      delete next.cardProduction?.costPerNewCard?.ultimate;
      delete next.cardProduction?.physicalAdoption?.ultimate;
      delete next.referral?.tierMultiplier?.ultimate;
      delete next.startingUltimateMix;

      for (const sk of SCENARIO_KEYS) {
        const s = next.scenarios?.[sk];
        if (!s) continue;
        delete s.ultimateConv;
        delete s.churnUltimate;
        delete s.ultimateSpend;
        delete s.ultimateTrading;
        delete s.ultimateTopup;
        delete s.ultimateAiActual;
        delete s.ultimateWalletOperations;
      }

      next.version = '2.5.0';
      return next;
    },
  },
  {
    from: '2.5.0',
    to: '2.6.0',
    apply(old) {
      const next = JSON.parse(JSON.stringify(old));

      const charityShare = next.points?.redemptionMix?.charity;
      if (typeof charityShare === 'number') {
        next.points.redemptionMix.usdCash = (next.points.redemptionMix.usdCash ?? 0) + charityShare;
      }
      delete next.points?.redemptionMix?.charity;
      delete next.points?.costPerRedemption?.charity;

      next.version = '2.6.0';
      return next;
    },
  },
  {
    from: '2.6.0',
    to: '2.7.0',
    apply(old) {
      const next = JSON.parse(JSON.stringify(old));

      // Historical 2.7 tuning pass kept Pro at its then-current €7 price.
      // Later deployment baselines may intentionally reset stored assumptions.
      next.tiers.free = { ...next.tiers.free, ...DEFAULTS.tiers.free };
      next.tiers.pro = { ...next.tiers.pro, ...DEFAULTS.tiers.pro, price: 7, priceAnnual: 70 };
      next.channelMix = JSON.parse(JSON.stringify(DEFAULTS.channelMix));
      next.acquisition = JSON.parse(JSON.stringify(DEFAULTS.acquisition));
      next.stablecoin = JSON.parse(JSON.stringify(DEFAULTS.stablecoin));
      next.points = JSON.parse(JSON.stringify(DEFAULTS.points));
      next.cardProduction = JSON.parse(JSON.stringify(DEFAULTS.cardProduction));
      next.fixedOpexMonthly = DEFAULTS.fixedOpexMonthly;
      next.cardFundedTopupShare = DEFAULTS.cardFundedTopupShare;
      next.scenarios = JSON.parse(JSON.stringify(DEFAULTS.scenarios));
      next.startingUsers = DEFAULTS.startingUsers;
      next.startingProMix = DEFAULTS.startingProMix;

      next.version = '2.7.0';
      return next;
    },
  },
  {
    from: '2.7.0',
    to: '2.8.0',
    apply(old) {
      const next = JSON.parse(JSON.stringify(old));
      if (!next.drops) {
        next.drops = JSON.parse(JSON.stringify(DEFAULTS.drops));
      }
      next.version = '2.8.0';
      return next;
    },
  },
  {
    from: '2.8.0',
    to: '2.9.0',
    apply(old) {
      const next = JSON.parse(JSON.stringify(old));
      const previous = next.drops || {};
      const bill = previous.billDrops || {};
      const yieldDrops = previous.yieldDrops || {};
      const proPayback = previous.proPayback || {};
      const partner = previous.partnerDrops || {};

      const avgRewardAmount =
        bill.avgDropAmount ??
        yieldDrops.avgDropAmount ??
        partner.avgDropAmount ??
        DEFAULTS.drops.moneyBack.avgRewardAmount;
      const appAccountsPer10Reward =
        avgRewardAmount > 0
          ? ((bill.referralAppAccountsPerWinner ??
              yieldDrops.referralAppAccountsPerWinner ??
              partner.referralAppAccountsPerWinner ??
              0) *
              10) /
            avgRewardAmount
          : 0;

      next.drops = {
        moneyBack: {
          ...JSON.parse(JSON.stringify(DEFAULTS.drops.moneyBack)),
          pulsarMonthlyBudget: bill.monthlyBudget ?? 0,
          yieldShare: yieldDrops.yieldShare ?? 0,
          yieldMonthlyCap: yieldDrops.maxMonthlyBudget ?? 0,
          proPaybackMonthlyBudget: proPayback.monthlyBudget ?? 0,
          proPaybackEligibleShare: proPayback.eligibleProShare ?? 0,
          proPaybackRebate:
            proPayback.rebatePerEligiblePro ?? DEFAULTS.drops.moneyBack.proPaybackRebate,
          partnerSponsoredBudget: partner.sponsoredMonthlyBudget ?? 0,
          partnerCostShare: partner.pulsarCostShare ?? 0,
          avgRewardAmount,
          fullImpactMonthlyBudget:
            bill.fullImpactMonthlyBudget ??
            yieldDrops.fullImpactMonthlyBudget ??
            partner.fullImpactMonthlyBudget ??
            DEFAULTS.drops.moneyBack.fullImpactMonthlyBudget,
          appAccountsPer10Reward,
          cardActivationLift:
            bill.cardActivationLift ??
            yieldDrops.cardActivationLift ??
            partner.cardActivationLift ??
            0,
          proConversionLift:
            bill.proConversionLift ??
            yieldDrops.proConversionLift ??
            proPayback.proConversionLift ??
            partner.proConversionLift ??
            0,
          churnReduction:
            bill.churnReduction ??
            yieldDrops.churnReduction ??
            proPayback.proChurnReduction ??
            partner.churnReduction ??
            0,
        },
      };
      if (next.tiers?.free) {
        next.tiers.free.cashbackRate = DEFAULTS.tiers.free.cashbackRate;
        next.tiers.free.cashbackSpendCap = DEFAULTS.tiers.free.cashbackSpendCap;
      }
      if (next.tiers?.pro) {
        next.tiers.pro.cashbackRate = DEFAULTS.tiers.pro.cashbackRate;
        next.tiers.pro.cashbackSpendCap = DEFAULTS.tiers.pro.cashbackSpendCap;
      }

      next.version = '2.9.0';
      return next;
    },
  },
  {
    from: '2.9.0',
    to: '2.9.1',
    apply(old) {
      const next = JSON.parse(JSON.stringify(old));
      next.version = '2.9.1';
      return next;
    },
  },
  {
    from: '2.9.1',
    to: '2.10.0',
    apply(old) {
      const next = JSON.parse(JSON.stringify(old));
      if (typeof next.fixedOpexMonthly !== 'number' || next.fixedOpexMonthly === 0) {
        next.fixedOpexMonthly = DEFAULTS.fixedOpexMonthly;
      }
      next.version = '2.10.0';
      return next;
    },
  },
  {
    from: '2.10.0',
    to: '2.11.0',
    apply(old) {
      const next = JSON.parse(JSON.stringify(old));
      next.wirex = {
        ...DEFAULTS.wirex,
        ...(next.wirex || {}),
        termYears: 3,
        integrationFee: 0,
        kycVerificationFee: 0,
        transactionFeeRate: 0,
        interchangeShareY1: 0,
        interchangeShareY2: null,
        interchangeShareY3: null,
        tier1Share: 0.1,
        tier2Share: 0.3,
        tier3Share: 0.5,
        fxPassThrough: 0.01,
        floorY1: 0,
        floorY2: 3000,
        floorY3: 5000,
        disputeFee: 25,
        disputesPer1000Tx: 1,
        transactionsPerCardholderMo: 20,
        physicalCardMoq: 3000,
      };
      next.version = '2.11.0';
      return next;
    },
  },
  {
    from: '2.11.0',
    to: '2.12.0',
    apply(old) {
      const next = JSON.parse(JSON.stringify(old));
      next.wirex = {
        ...DEFAULTS.wirex,
        ...(next.wirex || {}),
        interchangeShareY1: 0,
        interchangeShareY2: null,
        interchangeShareY3: null,
        tier1Share: 0.1,
        tier2Share: 0.3,
        tier3Share: 0.5,
        tier1Cap: 25_000_000,
        tier2Cap: 100_000_000,
        fxPassThrough: 0.005,
      };
      next.version = '2.12.0';
      return next;
    },
  },
  {
    from: '2.12.0',
    to: '2.13.0',
    apply(old) {
      const next = JSON.parse(JSON.stringify(old));
      next.agenticInfra = {
        ...DEFAULTS.agenticInfra,
        ...(next.agenticInfra || {}),
        enabled: true,
        portalUpgradeFee: {
          ...DEFAULTS.agenticInfra.portalUpgradeFee,
          ...(next.agenticInfra?.portalUpgradeFee || {}),
          activeBusinesses: 125,
        },
        settlementTxFee: {
          ...DEFAULTS.agenticInfra.settlementTxFee,
          ...(next.agenticInfra?.settlementTxFee || {}),
          volumeMonthly: 35_000_000,
        },
        paymentStreamingFee: {
          ...DEFAULTS.agenticInfra.paymentStreamingFee,
          ...(next.agenticInfra?.paymentStreamingFee || {}),
          volumeMonthly: 8_000_000,
        },
        marketplaceFee: {
          ...DEFAULTS.agenticInfra.marketplaceFee,
          ...(next.agenticInfra?.marketplaceFee || {}),
          volumeMonthly: 3_000_000,
        },
      };
      if (
        typeof next.cardFundedTopupShare !== 'number' ||
        next.cardFundedTopupShare === 0.25 ||
        next.cardFundedTopupShare === 0.3
      ) {
        next.cardFundedTopupShare = DEFAULTS.cardFundedTopupShare;
      }
      next.version = '2.13.0';
      return next;
    },
  },
  {
    from: '2.13.0',
    to: '2.14.0',
    apply(old) {
      const next = JSON.parse(JSON.stringify(old));
      next.scenarios = {
        ...DEFAULTS.scenarios,
        ...(next.scenarios || {}),
        bull: {
          ...DEFAULTS.scenarios.bull,
          ...(next.scenarios?.bull || {}),
          proConv: 0.138,
        },
      };
      next.version = '2.14.0';
      return next;
    },
  },
  {
    from: '2.14.0',
    to: '2.15.0',
    apply(old) {
      const next = JSON.parse(JSON.stringify(old));
      next.channelMix = JSON.parse(JSON.stringify(DEFAULTS.channelMix));
      if (next.regions) {
        for (const key of Object.keys(next.regions)) {
          next.regions[key] = {
            ...next.regions[key],
            cacBlendedY1: CAC_Y1,
            cacBlendedY2: CAC_Y2,
            cacBlendedY3: CAC_Y3,
          };
        }
      }
      next.version = '2.15.0';
      return next;
    },
  },
];

/**
 * Walk migrations from stored.version up to DEFAULTS.version. Pure.
 * Returns null if no migration path exists.
 */
export function migrate(stored) {
  if (!stored || typeof stored !== 'object') return null;
  let cur = stored;
  let version = cur.version || '1.0.0';
  const guard = MIGRATIONS.length + 2;
  for (let i = 0; i < guard; i++) {
    if (version === DEFAULTS.version) return cur;
    const step = MIGRATIONS.find((m) => m.from === version);
    if (!step) return null;
    cur = step.apply(cur);
    version = step.to;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Loader (versioned localStorage)
// ---------------------------------------------------------------------------

const STORAGE_KEY = 'pulsar_econ_v2';
const LEGACY_KEY = 'pulsar_econ_v1';

function deepClone(o) {
  return JSON.parse(JSON.stringify(o));
}

function deepMerge(target, source) {
  if (!source || typeof source !== 'object' || Array.isArray(source)) return source ?? target;
  const out = Array.isArray(target) ? target.slice() : { ...target };
  for (const k of Object.keys(source)) {
    const sv = source[k];
    out[k] =
      sv && typeof sv === 'object' && !Array.isArray(sv) && k in target
        ? deepMerge(target[k], sv)
        : sv;
  }
  return out;
}

/**
 * Load inputs from a Storage-like object (default: window.localStorage).
 * Migrates legacy v1 blob if present. Falls back to DEFAULTS on any error.
 */
export function loadInputs(storage = typeof localStorage !== 'undefined' ? localStorage : null) {
  if (!storage) return deepClone(DEFAULTS);
  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      const migrated = parsed.version === DEFAULTS.version ? parsed : migrate(parsed);
      if (migrated) {
        const merged = deepMerge(deepClone(DEFAULTS), migrated);
        merged.version = DEFAULTS.version;
        if (parsed.version !== DEFAULTS.version) {
          try {
            storage.setItem(STORAGE_KEY, JSON.stringify(merged));
          } catch {
            /* noop */
          }
        }
        return merged;
      }
    }
    const legacy = storage.getItem(LEGACY_KEY);
    if (legacy) {
      const oldShape = JSON.parse(legacy);
      const wrapped = { ...oldShape, version: '1.0.0' };
      const migrated = migrate(wrapped);
      if (migrated) {
        const merged = deepMerge(deepClone(DEFAULTS), migrated);
        try {
          storage.setItem(STORAGE_KEY, JSON.stringify(merged));
          storage.removeItem(LEGACY_KEY);
        } catch {
          /* noop */
        }
        return merged;
      }
    }
    return deepClone(DEFAULTS);
  } catch {
    return deepClone(DEFAULTS);
  }
}

export function persistInputs(
  inputs,
  storage = typeof localStorage !== 'undefined' ? localStorage : null,
) {
  if (!storage) return;
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(inputs));
  } catch {
    /* noop */
  }
}
